import React, { forwardRef } from 'react';
import classNames from 'classnames';
import InfoMessage from './InfoMessage';

interface ToggleSwitchProps extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'size' | 'type'> {
	/** Text beside the switch. Omit for a bare control in a table cell, and pass `aria-label`. */
	label?: React.ReactNode;
	/** The second line under the label, for what the setting actually governs. */
	description?: string;
	size?: 'sm' | 'md';
}

const sizeClasses = {
	sm: {
		track: 'h-5 w-9',
		knob: 'h-4 w-4',
		knobOn: 'peer-checked:[&>span]:translate-x-4',
		label: 'text-sm',
		// The label is boxed to exactly the track's height and its text centred inside that box, so
		// the two line up whatever the ambient line-height is. Setting `leading-5` instead looks
		// equivalent and is not: the app's stylesheets set a 1.6 body line-height on their own
		// selectors and load after the utilities, so the utility loses and the text sits low.
		labelBox: 'h-5',
		gap: 'gap-2',
	},
	md: {
		track: 'h-6 w-11',
		knob: 'h-5 w-5',
		knobOn: 'peer-checked:[&>span]:translate-x-5',
		label: 'text-base',
		labelBox: 'h-6',
		gap: 'gap-3',
	},
} as const;

/**
 * An on/off switch, for a setting that takes effect as soon as it is flipped.
 *
 * <p>The difference from {@link CheckboxInput} is what the control promises. A checkbox is a value in
 * a form you are filling in and will submit; a switch is a thing that is either on or off right now.
 * Use this where the change saves itself, and a checkbox where it is one answer among several behind
 * a Save button.</p>
 *
 * <p>A real checkbox input under the skin, visually hidden the same way `CheckboxInput` hides its
 * own — so keyboard focus, space to toggle, form association and screen-reader semantics are the
 * browser's rather than reimplemented.</p>
 */
export const ToggleSwitch = forwardRef<HTMLInputElement, ToggleSwitchProps>(
	({ label, description, className, disabled, size = 'md', ...props }, ref) => {
		const s = sizeClasses[size];

		return (
			<label
				className={classNames(
					'relative inline-flex items-start',
					s.gap,
					disabled ? 'cursor-not-allowed' : 'cursor-pointer',
					className,
				)}
			>
				<input
					type="checkbox"
					ref={ref}
					// Hidden without `display: none` or `opacity-0 absolute`, which either removes it from
					// the accessibility tree or lets focus scroll the document. Mirrors CheckboxInput.
					className="absolute h-px w-px overflow-hidden whitespace-nowrap border-0 p-0 [clip:rect(0,0,0,0)] peer"
					disabled={disabled}
					{...props}
				/>
				<span
					className={classNames(
						'relative flex-shrink-0 rounded-full bg-gray-300 transition-colors',
						s.track,
						'peer-checked:bg-[#0180B5]',
						// The knob is moved from here, not from its own class list. Tailwind's `peer-*`
						// variants compile to the sibling combinator, and the knob is a *child* of this
						// span rather than a sibling of the input — so `peer-checked:translate-x-4` on
						// the knob itself never matched anything. Same arbitrary-variant trick
						// CheckboxInput uses to colour its tick.
						s.knobOn,
						'peer-disabled:opacity-50',
						'peer-focus-visible:ring-2 peer-focus-visible:ring-cyan-400 peer-focus-visible:ring-offset-1',
					)}
				>
					<span
						className={classNames(
							'absolute left-0.5 top-1/2 -translate-y-1/2 rounded-full bg-white shadow transition-transform',
							s.knob,
						)}
					/>
				</span>
				{(label != null || description != null) && (
					// A div rather than a span: InfoMessage renders a div, and a div inside a span is
					// invalid nesting that some resets lay out unpredictably.
					<div className="flex min-w-0 flex-1 flex-col">
						{label != null && (
							<span
								className={classNames(
									'flex items-center text-gray-700 peer-disabled:text-gray-400',
									s.labelBox,
									s.label,
								)}
							>
								{label}
							</span>
						)}
						{description != null && <InfoMessage className="mt-0">{description}</InfoMessage>}
					</div>
				)}
			</label>
		);
	},
);

ToggleSwitch.displayName = 'ToggleSwitch';

export default ToggleSwitch;
